import { Alert, AlertTitle, Typography } from '@mui/material'
import type { CustomContentProps } from 'notistack'
import { SnackbarContent, useSnackbar } from 'notistack'
import type { ReactNode } from 'react'
import { forwardRef } from 'react'
import type { CustomApiError } from '@/api/model/customApiError'
import {
	snackbar_error,
	snackbar_info,
	snackbar_success,
	snackbar_warning,
} from '@/integrations/paraglide/messages/_index.js'

interface ErrorSnackbarProps extends SnackbarProps {
	error?: CustomApiError
}

interface SnackbarProps extends CustomContentProps {
	message: ReactNode
}

export const ErrorSnackbar = forwardRef<HTMLDivElement, ErrorSnackbarProps>(
	function ErrorSnackbar(
		{ id, message, error, onClose }: Readonly<ErrorSnackbarProps>,
		ref,
	) {
		const { closeSnackbar } = useSnackbar()
		const handleClose = () => {
			onClose?.()
			closeSnackbar(id)
		}
		return (
			<SnackbarContent ref={ref} role="alert">
				<Alert severity="error" variant="filled" onClose={handleClose}>
					<AlertTitle>{message}</AlertTitle>
					<Typography variant="caption">
						<b>{error?.ErrorCode}:</b> {error?.Message}
					</Typography>
				</Alert>
			</SnackbarContent>
		)
	},
)

export const SuccessSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(
	function SuccessSnackbar(
		{ id, message, onClose }: Readonly<SnackbarProps>,
		ref,
	) {
		const { closeSnackbar } = useSnackbar()
		const handleClose = () => {
			onClose?.()
			closeSnackbar(id)
		}
		return (
			<Alert
				severity="success"
				variant="filled"
				ref={ref}
				onClose={handleClose}
			>
				<AlertTitle>{snackbar_success()}</AlertTitle>
				{message}
			</Alert>
		)
	},
)

export const DefaultErrorSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(
	function DefaultErrorSnackbar(
		{ id, message, onClose }: Readonly<SnackbarProps>,
		ref,
	) {
		const { closeSnackbar } = useSnackbar()
		const handleClose = () => {
			onClose?.()
			closeSnackbar(id)
		}
		return (
			<Alert
				severity="warning"
				variant="filled"
				ref={ref}
				onClose={handleClose}
			>
				<AlertTitle>{snackbar_error()}</AlertTitle>
				{message}
			</Alert>
		)
	},
)

export const WarningSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(
	function WarningSnackbar(
		{ id, message, onClose }: Readonly<SnackbarProps>,
		ref,
	) {
		const { closeSnackbar } = useSnackbar()
		const handleClose = () => {
			onClose?.()
			closeSnackbar(id)
		}
		return (
			<Alert
				severity="warning"
				variant="filled"
				ref={ref}
				onClose={handleClose}
			>
				<AlertTitle>{snackbar_warning()}</AlertTitle>
				{message}
			</Alert>
		)
	},
)

export const InfoSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(
	function InfoSnackbar(
		{ id, message, onClose }: Readonly<SnackbarProps>,
		ref,
	) {
		const { closeSnackbar } = useSnackbar()
		const handleClose = () => {
			onClose?.()
			closeSnackbar(id)
		}
		return (
			<Alert severity="info" variant="filled" ref={ref} onClose={handleClose}>
				<AlertTitle>{snackbar_info()}</AlertTitle>
				{message}
			</Alert>
		)
	},
)

export const DefaultSnackbar = forwardRef<HTMLDivElement, SnackbarProps>(
	function DefaultSnackbar(
		{ id, message, onClose }: Readonly<SnackbarProps>,
		ref,
	) {
		const { closeSnackbar } = useSnackbar()
		const handleClose = () => {
			onClose?.()
			closeSnackbar(id)
		}
		return (
			<Alert variant="filled" ref={ref} onClose={handleClose}>
				{message}
			</Alert>
		)
	},
)
